import {
  PERMISSIONS,
  SETTINGS_PERMISSION,
  type Permission,
} from "@shared/constants/permissions";
import type { UserType } from "@shared/types/user";
import { matchPath } from "react-router-dom";

import { hasAiSeat, hasPermission } from "~/utils/permissions";

/** One permission, or a list of which any one is enough. */
type RoutePermission = Permission | readonly Permission[];

/**
 * The permission each module's pages need, as react-router patterns. The first
 * match wins, so a more specific pattern goes above the module it sits in.
 * Pages not listed here (AI, emails, personal settings, the customer portal
 * workspace) are open to every workspace user. Everyone has a personal inbox;
 * "Company Emails" only decides whether company inboxes show up in it.
 */
const ROUTE_PERMISSIONS: [pattern: string, permission: RoutePermission][] = [
  // Procedures run inside a work order are procedure instances.
  [
    "/app/work-orders/:ticketId/procedure/*",
    PERMISSIONS.procedureInstances.view,
  ],
  [
    "/app/work/work-orders/:ticketId/procedure/*",
    PERMISSIONS.procedureInstances.view,
  ],
  ["/app/work/procedures/instance/*", PERMISSIONS.procedureInstances.view],
  // The list page has a tab for templates and one for filled procedures.
  [
    "/app/work/procedures",
    [PERMISSIONS.procedureTemplates.view, PERMISSIONS.procedureInstances.view],
  ],
  ["/app/work/procedures/*", PERMISSIONS.procedureTemplates.view],
  ["/app/procedures/*", PERMISSIONS.procedureTemplates.view],

  ["/app/work/work-orders/*", PERMISSIONS.workOrders.view],
  ["/app/work/my-work-orders/*", PERMISSIONS.workOrders.view],
  ["/app/my-work-orders/*", PERMISSIONS.workOrders.view],
  ["/app/work-orders/*", PERMISSIONS.workOrders.view],
  ["/app/work/archived-work-orders/*", PERMISSIONS.workOrders.view],
  // Planned events are preventive maintenance, which belongs to assets.
  ["/app/work/planned-events/*", PERMISSIONS.assets.view],
  ["/app/work/tickets/*", PERMISSIONS.tickets.view],
  ["/app/work/scheduler/*", PERMISSIONS.scheduler.view],
  ["/app/scheduler/*", PERMISSIONS.scheduler.view],
  ["/app/work/insights/*", PERMISSIONS.reports.view],
  ["/app/work/analytics/*", PERMISSIONS.reports.view],
  ["/app/work/quotes/*", PERMISSIONS.quotes.view],
  ["/app/work/invoices/*", PERMISSIONS.invoices.view],

  ["/app/asset-hub/inventory/*", PERMISSIONS.parts.view],
  ["/app/assets/inventory/*", PERMISSIONS.parts.view],
  ["/app/inventory/*", PERMISSIONS.parts.view],
  ["/app/asset-hub/my-products/*", PERMISSIONS.products.view],
  ["/app/asset-hub/asset-templates/*", PERMISSIONS.products.view],
  ["/app/assets/templates/*", PERMISSIONS.products.view],
  ["/app/assets/machines/templates/*", PERMISSIONS.products.view],
  ["/app/asset-hub/my-assets/*", PERMISSIONS.assets.view],
  ["/app/asset-hub/shared-assets/*", PERMISSIONS.assets.view],
  ["/app/asset-hub/shared/*", PERMISSIONS.assets.view],
  ["/app/assets/*", PERMISSIONS.assets.view],
  ["/app/asset-hub/companies/*", PERMISSIONS.companies.view],
  ["/app/asset-hub/connections/*", PERMISSIONS.companies.view],
  ["/app/asset-hub/locations/*", PERMISSIONS.companies.view],
  ["/app/asset-hub/connection-requests/*", PERMISSIONS.companies.view],
  ["/app/asset-hub/contacts/*", PERMISSIONS.contacts.view],
  ["/app/customer-portal/*", PERMISSIONS.portals.view],

  ["/app/settings/company/*", SETTINGS_PERMISSION],
];

/**
 * Pages inside a guarded section that every workspace user may open. The email
 * sign-in callback lands under company settings for personal accounts too.
 */
const OPEN_PATHS = ["/app/settings/company/email-configuration/success"];

/** The permission a page needs, or null when any workspace user may open it. */
export const permissionForPath = (pathname: string): RoutePermission | null =>
  OPEN_PATHS.some((pattern) => matchPath(pattern, pathname))
    ? null
    : ROUTE_PERMISSIONS.find(([pattern]) =>
        matchPath(pattern, pathname),
      )?.[1] ?? null;

/**
 * A work order's AI notes are AI Notetaker content, so on top of the work
 * order they need the user's own AI seat, like the AI pages.
 */
const WORK_ORDER_AI_NOTES = "/app/work/work-orders/:workOrderId/ai-notes/*";

/**
 * A procedure opened inside its work order shows that work order around it,
 * so it needs View Work Orders on top of the procedure. Detached procedures
 * (`/app/work/procedures/instance/*`) belong to no work order.
 */
const WORK_ORDER_PROCEDURES = [
  "/app/work-orders/:ticketId/procedure/*",
  "/app/work/work-orders/:ticketId/procedure/*",
];

export const canOpenPath = (
  user: Pick<UserType, "permissions" | "productAccess"> | null | undefined,
  pathname: string,
) => {
  const permission = permissionForPath(pathname);
  const anyOf = typeof permission === "string" ? [permission] : permission;
  if (anyOf && !anyOf.some((each) => hasPermission(user, each))) return false;
  if (
    WORK_ORDER_PROCEDURES.some((pattern) => matchPath(pattern, pathname)) &&
    !hasPermission(user, PERMISSIONS.workOrders.view)
  ) {
    return false;
  }
  return !matchPath(WORK_ORDER_AI_NOTES, pathname) || hasAiSeat(user);
};

/** AI pages need the user's own AI seat, on top of the workspace's AI product. */
const AI_PAGES = "/app/ai/*";

/**
 * Whether a navigation link is hidden: modules the role can't open, and AI
 * modules for people without an AI seat, stay out of the menu. Opening one by
 * URL still shows the matching access modal.
 */
export const isHiddenInNavigation = (
  user: Pick<UserType, "permissions" | "productAccess"> | null | undefined,
  pathname: string,
) =>
  !canOpenPath(user, pathname) ||
  (!!matchPath(AI_PAGES, pathname) && !hasAiSeat(user));

/** Where a user lands, in order of preference. */
const LANDING_PATHS = [
  "/app/asset-hub/my-assets",
  "/app/work/work-orders",
  "/app/work/tickets",
  "/app/asset-hub/my-products",
  "/app/asset-hub/inventory",
  "/app/work/procedures",
  "/app/asset-hub/companies",
  "/app/asset-hub/contacts",
  "/app/work/emails",
  "/app/work/scheduler",
  "/app/work/planned-events",
  "/app/work/insights",
  "/app/customer-portal/my-portals",
  "/app/work/quotes",
  "/app/work/invoices",
];

/**
 * The first module the user can open, out of the pages that exist for their
 * workspace (`routePaths`, when given). A role with none of them still has its
 * own profile.
 */
export const landingPath = (
  user: Pick<UserType, "permissions" | "productAccess"> | null | undefined,
  routePaths?: string[],
) =>
  LANDING_PATHS.find(
    (path) =>
      (!routePaths || routePaths.includes(path)) && canOpenPath(user, path),
  ) ?? "/app/settings/personal/profile";
